import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Activity, Users, MousePointer, RefreshCw, Trash2,
  Monitor, Smartphone, Tablet, Bot, ShieldAlert, Globe2, Search,
  Filter, ChevronDown, ChevronRight, TrendingDown,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  ComposableMap, Geographies, Geography, Marker,
} from 'react-simple-maps';
import { Button } from '../../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Input } from '../../components/ui/input';

import { API_BASE as API } from '../../config';

// World topology source served by react-simple-maps; CDN-hosted, ~70KB, no auth.
const WORLD_TOPO = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

const RANGE_OPTIONS = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 365, label: 'Last 12 months' },
];

const DEVICE_ICON = {
  Mobile: Smartphone,
  Tablet: Tablet,
  Desktop: Monitor,
  Bot: Bot,
};

function StatCard({ icon: Icon, label, value, accent = 'text-emerald-400', testid }) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-lg p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-slate-400 text-xs uppercase tracking-wider mb-1">
        <Icon className={`w-3.5 h-3.5 ${accent}`} /> {label}
      </div>
      <p className="text-3xl font-bold text-white">{value}</p>
    </div>
  );
}

function WorldMap({ countries }) {
  // markerSize ∝ sqrt(count) so even a small visit still shows; bounded.
  const maxCount = countries.reduce((m, c) => Math.max(m, c.count), 1);
  return (
    <ComposableMap projectionConfig={{ scale: 145 }} width={900} height={420} style={{ width: '100%', height: 'auto' }}>
      <Geographies geography={WORLD_TOPO}>
        {({ geographies }) =>
          geographies.map((geo) => (
            <Geography
              key={geo.rsmKey}
              geography={geo}
              fill="#1e293b"
              stroke="#334155"
              strokeWidth={0.4}
              style={{
                default: { outline: 'none' },
                hover: { fill: '#334155', outline: 'none' },
                pressed: { outline: 'none' },
              }}
            />
          ))
        }
      </Geographies>
      {countries.filter(c => c.lat != null && c.lon != null).map((c) => {
        const r = 4 + 14 * Math.sqrt(c.count / maxCount);
        return (
          <Marker key={`${c.country}-${c.lat}-${c.lon}`} coordinates={[c.lon, c.lat]}>
            <circle r={r} fill="#10b981" fillOpacity={0.35} stroke="#10b981" strokeWidth={1.5}>
              <title>{`${c.country}: ${c.count}`}</title>
            </circle>
            <text textAnchor="middle" y={-r - 2} className="fill-white" style={{ fontSize: 9, fontWeight: 600, pointerEvents: 'none' }}>
              {c.count > 2 ? c.count : ''}
            </text>
          </Marker>
        );
      })}
    </ComposableMap>
  );
}

/** Strict drop-off funnel. Each step is a subset of the previous one.
 *  Bar width is the share of the top-of-funnel count, so widths fall
 *  monotonically — visually obvious where the biggest drop is. */
function Funnel({ steps }) {
  if (!steps?.length) return null;
  const top = steps[0]?.count || 1;
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-lg p-4" data-testid="funnel-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <Filter className="w-4 h-4 text-emerald-400" /> Demo Funnel
        </h3>
        <span className="text-xs text-slate-500">Unique visitors per step</span>
      </div>
      <div className="space-y-2.5">
        {steps.map((s, i) => {
          const pct = top > 0 ? (s.count / top) * 100 : 0;
          const hasDrop = i > 0 && s.drop_off_from_prev > 0;
          return (
            <div key={s.step} className="group" data-testid={`funnel-step-${s.step.toLowerCase().replace(/\s/g, '-')}`}>
              <div className="flex items-center justify-between text-xs mb-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-white">{s.step}</span>
                  <span className="text-slate-500 hidden sm:inline">· {s.hint}</span>
                </div>
                <div className="flex items-center gap-3 font-mono">
                  {hasDrop && (
                    <span className="text-rose-400 inline-flex items-center gap-0.5" title={`${s.drop_off_from_prev}% drop from prev step`}>
                      <TrendingDown className="w-3 h-3" /> {s.drop_off_from_prev}%
                    </span>
                  )}
                  <span className="text-slate-400">{s.pct_of_top}%</span>
                  <span className="text-white font-semibold w-10 text-right">{s.count}</span>
                </div>
              </div>
              <div className="relative h-7 bg-slate-950 rounded overflow-hidden border border-slate-800">
                <div
                  className={`h-full rounded transition-all ${i === 0 ? 'bg-emerald-500' : i === steps.length - 1 ? 'bg-blue-500' : 'bg-emerald-500/70'}`}
                  style={{ width: `${Math.max(pct, 1)}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-slate-600 mt-3 leading-relaxed">
        Each step counts unique <code>user_id</code>s and is a strict subset of the step above it,
        so drop-off percentages reflect real journey friction (not just different events).
      </p>
    </div>
  );
}

function deviceIcon(t) {
  const I = DEVICE_ICON[t] || Monitor;
  return <I className="w-3.5 h-3.5 text-slate-400" />;
}

function formatEventDetail(e) {
  const loc = e.meta?.location;
  const dev = e.meta?.device;
  const parts = [];
  if (loc?.city) parts.push(`${loc.city}${loc.country_code ? ', ' + loc.country_code : ''}`);
  if (dev?.summary) parts.push(dev.summary);
  if (loc?.is_vpn || loc?.is_proxy || loc?.is_tor) parts.push('⚑ VPN');
  return parts.join(' · ') || '—';
}

export default function DevDemoAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [search, setSearch] = useState('');
  // Persist map collapse state so it stays the way the user left it.
  const [mapOpen, setMapOpen] = useState(() => {
    const stored = window.localStorage.getItem('demoAnalytics.mapOpen');
    return stored === null ? true : stored === 'true';
  });
  useEffect(() => { window.localStorage.setItem('demoAnalytics.mapOpen', String(mapOpen)); }, [mapOpen]);

  const fetchData = useCallback(async (rangeDays) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/dev/demo-analytics?days=${rangeDays}`, { withCredentials: true });
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(days); }, [fetchData, days]);

  const handleReset = async () => {
    if (!window.confirm('Reset demo tenant data? This deletes claims created by the demo user.')) return;
    try {
      const res = await axios.post(`${API}/dev/demo-reset/tenant_a74c8bc7c4da`, {}, { withCredentials: true });
      toast.success(res.data.message);
    } catch { toast.error('Reset failed'); }
  };

  const filteredEvents = useMemo(() => {
    const events = data?.recent_events || [];
    if (!search.trim()) return events;
    const s = search.toLowerCase();
    return events.filter((e) => {
      const hay = [
        e.event, e.meta?.location?.city, e.meta?.location?.country,
        e.meta?.device?.summary, e.meta?.device?.browser, e.meta?.device?.os,
        e.meta?.location?.org, e.user_id,
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(s);
    });
  }, [data, search]);

  if (loading && !data) return <div className="text-center py-12 text-slate-400">Loading analytics…</div>;
  if (!data) return <div className="text-center py-12 text-slate-400">No analytics data available</div>;

  const maxBar = Math.max(...(data.daily_logins || []).map(d => d.count), 1);

  return (
    <div className="text-slate-200">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Activity className="w-6 h-6 text-emerald-400" />
          <div>
            <h1 className="text-2xl font-bold text-white">Demo Analytics</h1>
            <p className="text-sm text-slate-400">Track public demo usage</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="w-[160px] h-9 bg-slate-900 border-slate-700 text-slate-200" data-testid="analytics-range-trigger">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 text-slate-200">
              {RANGE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={String(o.value)} data-testid={`analytics-range-${o.value}`}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="ghost" size="sm" onClick={() => fetchData(days)} className="text-slate-400 hover:text-white" data-testid="refresh-analytics">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button variant="outline" size="sm" className="border-red-800 text-red-400 hover:bg-red-900/30" onClick={handleReset} data-testid="reset-demo-data">
            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Reset Demo Data
          </Button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <StatCard icon={Users} label="Logins" value={data.total_logins} testid="stat-total-logins" />
        <StatCard icon={Users} label="Unique Visitors" value={data.unique_visitors} accent="text-blue-400" testid="stat-unique-visitors" />
        <StatCard icon={MousePointer} label="Total Events" value={data.total_events} testid="stat-total-events" />
        <StatCard icon={Activity} label="Event Types" value={data.events_by_type?.length || 0} testid="stat-event-types" />
        <StatCard icon={ShieldAlert} label="VPN / Proxy" value={data.suspicious || 0} accent={(data.suspicious || 0) > 0 ? 'text-amber-400' : 'text-slate-500'} testid="stat-suspicious" />
      </div>

      {/* World map (collapsible) */}
      {(data.countries || []).length > 0 && (
        <div className="bg-slate-950 border border-slate-800 rounded-lg mb-6" data-testid="world-map">
          <button
            onClick={() => setMapOpen(o => !o)}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-900 transition-colors rounded-t-lg"
            data-testid="world-map-toggle"
          >
            <h3 className="text-sm font-semibold text-white flex items-center gap-2">
              {mapOpen ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
              <Globe2 className="w-4 h-4 text-emerald-400" /> Geographic distribution
              <span className="text-xs text-slate-500 font-normal ml-1">({data.countries.length} countries)</span>
            </h3>
            {!mapOpen && (
              <span className="text-xs text-slate-500 hidden sm:inline">
                {data.countries.slice(0, 3).map(c => `${c.country_code || c.country?.slice(0, 2)} ${c.count}`).join(' · ')}
                {data.countries.length > 3 ? ` · +${data.countries.length - 3} more` : ''}
              </span>
            )}
          </button>
          {mapOpen && (
            <div className="px-3 pb-3">
              <WorldMap countries={data.countries} />
            </div>
          )}
        </div>
      )}

      {/* Funnel — what % of visitors actually engage end-to-end */}
      {data.funnel?.length > 0 && (
        <div className="mb-6">
          <Funnel steps={data.funnel} />
        </div>
      )}

      {/* Daily logins + Events by type */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-white mb-3">Daily Logins ({days}d)</h3>
          <div className="flex items-end gap-1 h-32" data-testid="daily-logins-chart">
            {(data.daily_logins || []).map(d => (
              <div key={d.date} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: ${d.count}`}>
                <span className="text-[9px] text-slate-500">{d.count}</span>
                <div className="w-full bg-emerald-500/80 rounded-t" style={{ height: `${(d.count / maxBar) * 100}%`, minHeight: 4 }} />
                <span className="text-[8px] text-slate-600 -rotate-45 origin-top-left w-0 whitespace-nowrap">{d.date.slice(5)}</span>
              </div>
            ))}
            {(!data.daily_logins?.length) && <p className="text-sm text-slate-500 w-full text-center">No login data yet</p>}
          </div>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-white mb-3">Events by Type</h3>
          <div className="space-y-2">
            {(data.events_by_type || []).slice(0, 10).map(e => (
              <div key={e.event} className="flex items-center gap-3">
                <span className="text-xs text-slate-400 w-40 truncate font-mono">{e.event}</span>
                <div className="flex-1 bg-slate-800 rounded-full h-2 overflow-hidden">
                  <div className="bg-emerald-500 h-full rounded-full" style={{ width: `${(e.count / (data.total_events || 1)) * 100}%` }} />
                </div>
                <span className="text-xs text-slate-300 w-8 text-right font-semibold">{e.count}</span>
              </div>
            ))}
            {(!data.events_by_type?.length) && <p className="text-sm text-slate-500">No events yet</p>}
          </div>
        </div>
      </div>

      {/* Device / Browser / Top Cities */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-4" data-testid="devices-panel">
          <h3 className="text-sm font-semibold text-white mb-3">Top Devices</h3>
          <div className="space-y-2">
            {(data.devices || []).slice(0, 10).map((d, i) => (
              <div key={i} className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  {deviceIcon(d.type)}
                  <span className="text-sm text-slate-300 truncate">{d.label}</span>
                </div>
                <span className="text-sm font-semibold text-white shrink-0">{d.count}</span>
              </div>
            ))}
            {!data.devices?.length && <p className="text-sm text-slate-500">No data yet</p>}
          </div>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-lg p-4" data-testid="browsers-panel">
          <h3 className="text-sm font-semibold text-white mb-3">Browsers</h3>
          <div className="space-y-2">
            {(data.browsers || []).map(b => (
              <div key={b.browser} className="flex items-center justify-between">
                <span className="text-sm text-slate-300 truncate max-w-[200px]">{b.browser}</span>
                <span className="text-sm font-semibold text-white">{b.count}</span>
              </div>
            ))}
            {!data.browsers?.length && <p className="text-sm text-slate-500">No data yet</p>}
          </div>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-lg p-4" data-testid="locations-panel">
          <h3 className="text-sm font-semibold text-white mb-3">Top Cities</h3>
          <div className="space-y-2">
            {(data.locations || []).slice(0, 10).map((l, i) => (
              <div key={i} className="flex items-center justify-between">
                <span className="text-sm text-slate-300 truncate max-w-[220px]">
                  {l.city}{l.region ? `, ${l.region}` : ''}{l.country ? `, ${l.country}` : ''}
                </span>
                <span className="text-sm font-semibold text-white">{l.count}</span>
              </div>
            ))}
            {!data.locations?.length && <p className="text-sm text-slate-500">No data yet</p>}
          </div>
        </div>
      </div>

      {/* Recent events — parsed columns + search */}
      <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3 gap-3">
          <h3 className="text-sm font-semibold text-white">Recent Events</h3>
          <div className="relative w-72">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
            <Input
              placeholder="Filter by event, city, device, user…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 pl-8 bg-slate-950 border-slate-700 text-slate-200 placeholder:text-slate-600"
              data-testid="events-search"
            />
          </div>
        </div>
        <div className="overflow-x-auto max-h-80 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-slate-900">
              <tr className="text-slate-500 uppercase tracking-wider border-b border-slate-800">
                <th className="text-left py-2 px-3">Time</th>
                <th className="text-left py-2 px-3">Event</th>
                <th className="text-left py-2 px-3">Location</th>
                <th className="text-left py-2 px-3">Device</th>
                <th className="text-left py-2 px-3">Network</th>
                <th className="text-right py-2 px-3">Flags</th>
              </tr>
            </thead>
            <tbody>
              {filteredEvents.map((e, i) => {
                const loc = e.meta?.location || {};
                const dev = e.meta?.device || {};
                const flags = [];
                if (loc.is_vpn) flags.push('VPN');
                if (loc.is_proxy) flags.push('PROXY');
                if (loc.is_tor) flags.push('TOR');
                if (loc.is_hosting) flags.push('HOST');
                if (dev.is_bot) flags.push('BOT');
                return (
                  <tr key={i} className="border-b border-slate-800/50 hover:bg-slate-800/40" data-testid={`event-row-${i}`}>
                    <td className="py-2 px-3 text-slate-500 whitespace-nowrap">{new Date(e.created_at).toLocaleString()}</td>
                    <td className="py-2 px-3 font-mono text-emerald-400">{e.event}</td>
                    <td className="py-2 px-3 text-slate-300">
                      {loc.city || loc.country ? (
                        <span>
                          {loc.city}{loc.region && loc.city !== loc.region ? `, ${loc.region}` : ''}
                          {loc.country_code ? <span className="text-slate-500 ml-1">({loc.country_code})</span> : ''}
                        </span>
                      ) : <span className="text-slate-600">—</span>}
                    </td>
                    <td className="py-2 px-3 text-slate-300">
                      <div className="flex items-center gap-1.5">
                        {deviceIcon(dev.device)}
                        <span className="truncate max-w-[260px]" title={dev.summary || ''}>
                          {dev.summary || dev.device || '—'}
                        </span>
                      </div>
                    </td>
                    <td className="py-2 px-3 text-slate-500 truncate max-w-[180px]" title={loc.org || ''}>
                      {loc.org || '—'}
                    </td>
                    <td className="py-2 px-3 text-right">
                      {flags.length ? flags.map(f => (
                        <span key={f} className="inline-block ml-1 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-amber-500/15 text-amber-400 border border-amber-500/30">{f}</span>
                      )) : <span className="text-slate-700">·</span>}
                    </td>
                  </tr>
                );
              })}
              {!filteredEvents.length && (
                <tr><td colSpan={6} className="text-center py-8 text-slate-500">No events match</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {search && (
          <p className="text-xs text-slate-500 mt-2">
            Showing {filteredEvents.length} of {data.recent_events?.length || 0} events
          </p>
        )}
      </div>
    </div>
  );
}
